import React, { Component } from 'react';
import classnames from 'classnames';
import { ClassValue } from 'classnames/types';
import noop from 'lodash/noop';
import BodyText from '../Typography/BodyText/BodyText';
import Checkbox from '../Checkbox/Checkbox';
import Icon from '../Icon/Icon';
import Gutter from '../Gutter/Gutter';
import styles from './OptionList.module.scss';
import Flex from '../Flex/Flex';
import isIE11 from '../../shared/utils/isIE11/isIE11';
import BaseInput from '../Input/BaseInput';

export type OptionListOption = {
  label: string;
  value: string;
  disabled?: boolean;
  checked?: boolean;
};
// Aliases for backwards compatibility
export type OptionShape = OptionListOption;
export type MultiselectOptionShape = OptionListOption;

type OptionsListOptionWithRef = OptionListOption & {
  ref: React.RefObject<HTMLDivElement>;
};

export type OptionListProps = {
  /** List of options */
  options: OptionListOption[];
  /** Callback handler when user clicks one of the options */
  optionOnClick: (
    event: React.SyntheticEvent<HTMLDivElement>,
    option: OptionListOption
  ) => void;
  /** Moves the focus to the option which has the matching id */
  focusedOptionId?: string;
  /** blur event handler */
  onBlur?: (
    event:
      | React.FocusEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => void;
  /** mouse down handler */
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  /** class for component */
  className?: ClassValue;
  /** remove borderbottom from optionlist items */
  noBorderBottom?: boolean;
  /** Optional property to specify placeholder text displayed in the search input field */
  searchPlaceholder?: string;
};

type OptionListState = {
  options: OptionsListOptionWithRef[];
  focusableOptions: OptionsListOptionWithRef[];
  keyboardStyles: boolean;
  searchTerm: string;
};

class OptionList extends Component<OptionListProps, OptionListState> {
  constructor(props: OptionListProps) {
    super(props);

    const options = props.options.map((option) => {
      return {
        ...option,
        ref: React.createRef<HTMLDivElement>(),
      };
    });

    this.state = {
      options: options,
      focusableOptions: options.filter((option) => !option.disabled),
      keyboardStyles: false,
      searchTerm: '',
    };
  }

  inputElement = React.createRef<HTMLInputElement>();
  optionListElement = React.createRef<HTMLDivElement>();

  componentDidMount() {
    const { focusedOptionId, searchPlaceholder } = this.props;
    const { focusableOptions } = this.state;

    if (typeof focusedOptionId !== 'undefined') {
      const focusedOption = focusableOptions.find(
        (option) => option.value === focusedOptionId
      );

      focusedOption?.ref.current?.focus();
    }

    if (typeof searchPlaceholder !== 'undefined') {
      this.inputElement?.current?.focus();
    }
  }

  componentDidUpdate(prevProps: OptionListProps) {
    /* istanbul ignore else  */
    if (this.props.options !== prevProps.options) {
      const optionsWithRefs = this.props.options.map((option) => {
        return {
          ...option,
          ref: React.createRef<HTMLDivElement>(),
        };
      });

      this.setState({
        options: optionsWithRefs,
        focusableOptions: optionsWithRefs.filter((option) => !option.disabled),
      });
    }

    /* istanbul ignore else  */
    if (this.props.focusedOptionId !== prevProps.focusedOptionId) {
      const focusedOption = this.state.focusableOptions.find(
        (option) => option.value === this.props.focusedOptionId
      );

      focusedOption?.ref.current?.focus();
    }
  }

  findFocusedIndex = (focusedId: string) => {
    const { focusableOptions } = this.state;
    return focusableOptions.findIndex((option) => option.value === focusedId);
  };

  focusOneOptionDown = (focusedOptionIndex: number) => {
    const { focusableOptions } = this.state;
    const nextOption =
      focusedOptionIndex === focusableOptions.length - 1
        ? focusableOptions.find((option, index) => index === 0)
        : focusableOptions.find(
            (option, index) => index === focusedOptionIndex + 1
          );

    nextOption?.ref.current?.focus();
  };

  focusOneOptionUp = (focusedOptionIndex: number) => {
    const { focusableOptions } = this.state;
    const nextOption =
      focusedOptionIndex === 0
        ? focusableOptions.find(
            (option, index) => index === focusableOptions.length - 1
          )
        : focusableOptions.find(
            (option, index) => index === focusedOptionIndex - 1
          );

    nextOption?.ref.current?.focus();
  };

  keyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const focusedOptionIndex = this.findFocusedIndex(event.currentTarget.id);

    // Enable keyboard focus styles
    if (
      event.key === 'Tab' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown'
    ) {
      this.setState({
        keyboardStyles: true,
      });
    }

    if (event.shiftKey && event.key === 'Tab') {
      event.preventDefault();
      this.focusOneOptionUp(focusedOptionIndex);
    } else {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          this.focusOneOptionUp(focusedOptionIndex);
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.focusOneOptionDown(focusedOptionIndex);
          break;
        case 'Escape':
          event.preventDefault();
          this.props.onBlur?.(event);
          break;
        case 'Enter':
          event.preventDefault();
          this.handleOptionOnClick(event);
          break;
        case 'Tab':
          event.preventDefault();
          this.focusOneOptionDown(focusedOptionIndex);
          break;
        default:
          break;
      }
    }
  };

  handleOptionOnClick = (event: React.SyntheticEvent<HTMLDivElement>) => {
    event.preventDefault(); // If target is a input, prevent parent event triggering.
    const { optionOnClick } = this.props;

    const selectedId = event.currentTarget.dataset.id;

    const clickedOptionObject = this.state.focusableOptions.find(
      (option) => option.value === selectedId
    );

    /* istanbul ignore else  */
    if (clickedOptionObject) {
      clickedOptionObject.ref.current?.focus();

      optionOnClick(event, clickedOptionObject);
    }
  };

  triggerClickOutside = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      this.optionListElement.current &&
      !this.optionListElement.current.contains(
        event.relatedTarget as HTMLElement
      )
    ) {
      /* In IE prevent the onBlur */
      /* istanbul ignore else  */
      if (!isIE11) {
        this.props.onBlur?.(event);
      }
    }
  };

  setSearchTerm = (searchTerm) => {
    this.setState({
      searchTerm: searchTerm,
      options: this.props.options
        .filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map((option) => ({
          ...option,
          ref: React.createRef<HTMLDivElement>(),
        })),
    });
  };

  render() {
    const { options, keyboardStyles, searchTerm } = this.state;

    return (
      <div
        style={{ marginTop: '4px' }}
        className={classnames([
          styles.optionlist,
          this.props.className,
          {
            [styles.optionlist__showKeyboardFocus]: keyboardStyles,
            [styles.optionlist_borderlessItems]: this.props.noBorderBottom,
          },
        ])}
        onBlur={this.triggerClickOutside}
        onMouseDown={this.props.onMouseDown}
        ref={this.optionListElement}
      >
        {this.props.searchPlaceholder && (
          <BaseInput
            type="text"
            data-testid="search-input"
            placeholder={this.props.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => this.setSearchTerm(e.target.value)}
            className={styles.searchInput}
            ref={this.inputElement}
          />
        )}
        <div className={classnames([styles.optionlistInnerContainer])}>
          {options.map((option, index) => {
            const { disabled, value, label, ref, checked } = option;

            const classNames = [
              styles.optionlist_item,
              'optionList__optionId',
              {
                [styles.optionlist_item__selected]:
                  this.props.focusedOptionId === value || checked,
                [styles.optionlist_item__disabled]: option.disabled,
              },
            ];

            const isCheckable = typeof checked === 'boolean';
            const fontWeight =
              this.props.focusedOptionId === value
                ? 'bold'
                : checked
                ? 'bold'
                : undefined;

            return (
              <div
                key={index}
                className={classnames(classNames)}
                tabIndex={disabled ? -1 : 0}
                onClick={disabled ? undefined : this.handleOptionOnClick}
                onKeyDown={disabled ? undefined : this.keyDown}
                ref={disabled ? undefined : ref}
                data-id={value}
                id={value}
              >
                {isCheckable ? (
                  <Flex className={styles.optionlist_itemContent}>
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onChange={noop}
                    />
                    <Gutter size="xs" />
                    <BodyText tagName="span" fontWeight={fontWeight}>
                      {label}
                    </BodyText>
                  </Flex>
                ) : (
                  <Flex centeredVertically spaceBetween>
                    <BodyText tagName="span" fontWeight={fontWeight}>
                      {label}
                    </BodyText>
                    <Icon
                      hidden={this.props.focusedOptionId !== value}
                      name="checkmark"
                      color="purple500"
                      size="sm"
                    />
                  </Flex>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

export default OptionList;
